import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IAbstractProductIncluded } from '../../../models/abstract-product.models';
import { IContractParams } from '../../../models/catalog.models';
import { MarketingFacade } from '../../../facades/marketing.facade';
import { CatalogFacade } from '../../../facades/catalog.facade';
import { AnalyticsService } from '../../../analytics/analytics.service';
import { environment } from '../../../../environments/environment';
import { EProductDetails } from '../../../configurations/product-details';
import { ProductUtils } from '../../../utils/product.utils';
import { CpqUtils } from '../../../utils/cpq.utils';
import { IAddToCartAdditionalFields } from '../../../models/cart.models';
import { EInstalledBaseTabs } from '../../../configurations/common';
import { ISimpleInstallBaseProduct } from '../../../models/installedbase.models';

@Component({
  selector: 'app-configurable-product-pdp',
  templateUrl: './configurable-product-pdp.component.html',
  styleUrls: ['./configurable-product-pdp.component.scss'],
  standalone: false,
})
export class ConfigurableProductPdpComponent {
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly catalogFacade: CatalogFacade = inject(CatalogFacade);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);

  openZoomedPicture: boolean = false;

  @Input() sku!: string;
  @Input() productInfoLoaded!: boolean;
  @Input() abstractProduct!: any;
  @Input() defaultImg!: string;
  @Input() mainPicture!: string;
  @Input() productDetailsEnum: typeof EProductDetails = EProductDetails;
  @Input() installBaseProducts: ISimpleInstallBaseProduct[] = [];
  @Input() displayConfig: EProductDetails[] = [];
  @Input() contractParams: IContractParams = {};
  @Input() flNumber: string | null = null;
  @Input() materialNumber: string | null = null;
  @Input() stepNumber!: number;
  @Input() concreteProduct: any;
  @Output() addProductToCartSubmit: EventEmitter<IAddToCartAdditionalFields> = new EventEmitter<IAddToCartAdditionalFields>();

  isConfigurable(): boolean {
    return this.concreteProduct ? this.concreteProduct.attributes.productConfigurationInstance !== null : false;
  }

  isServiceUnderConfiguration(): boolean {
    return !!this.flNumber && !!this.materialNumber && this.isConfigurable();
  }

  addProductToCart(): void {
    this.addProductToCartSubmit.emit({addSystemDetails: true});
  }

  openZoomPicture(): void {
    if (this.mainPicture !== this.defaultImg) {
      this.openZoomedPicture = !this.openZoomedPicture;
      if (this.openZoomedPicture) {
        this.analyticsService.setProducts(this.abstractProduct);
        this.analyticsService.trackProduct('image.enlarge', this.abstractProduct);
      }
    }
  }

  getDetailAttribute(attributeValue: string | null): string {
    return ProductUtils.getDetailAttribute(attributeValue);
  }

  tracking(name: string): void {
    this.analyticsService.setProducts(this.abstractProduct);
    this.analyticsService.trackAccordion(name);
  }

  trackInstalledBase(installedBase: ISimpleInstallBaseProduct): void {
    this.catalogFacade.setInstalledBase(installedBase);
    this.analyticsService.trackInstalledBase(installedBase.attributes.materialName, installedBase.id ?? '');
  }

  displayProductDetail(section: EProductDetails): boolean {
    return this.displayConfig.includes(section);
  }

  hasTranslation(key: string): boolean {
    const translation: string = this.translate.instant(key);
    return !!translation && translation !== key;
  }

  getCpqItemsAttributes(): string[] {
    return this.concreteProduct?.attributes.superAttributesDefinition?.filter((el: string): boolean => {
      return environment.cpqItemAttributesToHide.indexOf(el) < 0;
    }) ?? [];
  }

  isContactUsButtonDisplay(): boolean {
    return this.isConfigurable() && this.concreteProduct?.attributes.attributes?.available === '0';
  }

  cpqButtonText(): string {
    const selectOption: string = this.isServiceUnderConfiguration() ? 'app-product-page.configure-online' : 'app-product-page.select-equipment';
    return this.isContactUsButtonDisplay() ? 'catalog.purchase-offline' : selectOption;
  }

  cpqButtonAction(product: IAbstractProductIncluded): void {
    if (this.isContactUsButtonDisplay()) {
      this.contactUsForm(product);
    } else {
      this.cpqServiceAction();
    }
  }

  getExtendedContractParams(product: IAbstractProductIncluded): IContractParams {
    return {
      ...CpqUtils.filterOutEmptyParams(this.contractParams),
      selectedService: product.attributes.name ?? ''
    };
  }

  showContractStepsBar(): boolean {
    return this.productInfoLoaded && !!this.flNumber && !!this.materialNumber;
  }

  private cpqServiceAction(): void {
    if (this.isServiceUnderConfiguration()) {
      this.cpqStartConfiguration();
    } else {
      this.selectEquipment();
    }
  }

  private contactUsForm(product: IAbstractProductIncluded): void {
    const url: string = this.router.createUrlTree(
      ['/contact-us'],
      {queryParams: this.getExtendedContractParams(product)},
    ).toString();

    this.router.navigateByUrl(url).then();
  }

  private cpqStartConfiguration(): void {
    this.marketingFacade.startContractConfiguration(this.sku, this.flNumber ?? '', this.materialNumber ?? '');
  }

  private selectEquipment(): void {
    this.router.navigate(['/my-installed-base'], {queryParams: {selectedService: this.sku, tab: EInstalledBaseTabs.CONTRACTS}}).then();
  }
}
